import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { AlertTriangle, Info } from "react-feather";
import styles from "./billing.module.css";
import { StorageGetItem, STORAGE_KEYS } from "./StorageComp";
import { formatGBP2 } from "./Features";

// Open-invoice reminder above company admin pages: due date, then the grace-period countdown
const BillingBanner = () => {
  const [auth, setAuth] = useState(() => StorageGetItem(STORAGE_KEYS.AUTH));

  useEffect(() => {
    const onUpdate = () => setAuth(StorageGetItem(STORAGE_KEYS.AUTH));
    window.addEventListener("efl-billing-updated", onUpdate);
    return () => window.removeEventListener("efl-billing-updated", onUpdate);
  }, []);

  if (!auth || auth?.user_type === 1 || auth?.user_type === 5) return null;
  const subscription = auth?.subscription;
  if (!subscription || subscription.status === "suspended" || subscription.status === "cancelled") return null;

  const open = subscription.outstanding || [];

  // Unsubscribed: remind until the service ends (an overdue invoice takes priority)
  if (subscription.cancels_at && !open.some((i) => i.status === "overdue")) {
    return (<div className={`${styles.Banner} ${styles.BannerWarn}`} role="status">
      <Info />
      <span>Your subscription ends on {dayjs(subscription.cancels_at).format("DD MMM YYYY")}. Changed your mind?</span>
      {auth?.user_type === 2 && <Link to={"/company-profile?tab=billing"}>Keep my subscription</Link>}
    </div>);
  }

  if (open.length === 0) return null;

  // The invoice closest to suspension decides the banner
  const first = [...open].sort((a, b) => dayjs(a.due_date).diff(dayjs(b.due_date)))[0];
  const total = open.reduce((sum, i) => sum + Number(i.total || 0), 0);
  const daysLeft = dayjs(first.suspend_on).startOf("day").diff(dayjs().startOf("day"), "day");
  const isOverdue = first.status === "overdue";
  const critical = isOverdue && daysLeft <= 2;
  const link = auth?.user_type === 2 ? <Link to={"/company-profile?tab=billing"}>View invoice</Link> : null;

  let tone = styles.BannerInfo;
  let message = `Invoice ${first.number} for ${formatGBP2(first.total)} is due on ${dayjs(first.due_date).format("DD MMM YYYY")}.`;

  if (isOverdue) {
    tone = critical ? styles.BannerDanger : styles.BannerWarn;
    const when = daysLeft <= 0 ? "today" : `on ${dayjs(first.suspend_on).format("DD MMM YYYY")}`;
    message = `${open.length > 1 ? `${open.length} invoices (${formatGBP2(total)}) are` : `Invoice ${first.number} (${formatGBP2(first.total)}) is`} overdue. Service will stop ${when} unless payment is received.`;
  }

  return (<div className={`${styles.Banner} ${tone}`} role="status">
    {isOverdue ? <AlertTriangle /> : <Info />}
    <span>{message}</span>
    {link}
  </div>);
}

export default BillingBanner;
